import { useEffect } from 'react';
import { useConfirmStore } from '@/store/confirmStore';
import { useScrollLock } from '@/hooks/useScrollLock';

/**
 * 브라우저 confirm() 을 대체하는 커스텀 모달.
 * 사용법:
 *   import { confirmModal } from '@/store/confirmStore';
 *   const ok = await confirmModal({ message: '정말 삭제할까요?' , tone: 'danger' });
 *   if (!ok) return;
 */
export function ConfirmModal() {
  const { open, options, resolve } = useConfirmStore();

  useScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resolve(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open, resolve]);

  if (!open || !options) return null;

  const {
    title,
    message,
    confirmLabel = '확인',
    cancelLabel = '취소',
    tone = 'primary',
    alertOnly = false,
  } = options;

  const confirmCls =
    tone === 'danger'
      ? 'bg-[var(--color-fall)] text-white hover:brightness-95'
      : 'bg-[#62b058] text-white hover:brightness-95';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-5"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) resolve(false);
      }}
    >
      <div className="w-full max-w-sm rounded-2xl bg-[var(--color-surface)] p-6 shadow-xl">
        {title && (
          <h2 className="mb-2 text-base font-bold text-[var(--color-text)]">{title}</h2>
        )}
        <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)]">
          {message}
        </p>
        <div className="mt-6 flex gap-2">
          {/* alertOnly 면 취소 버튼 숨김 — 단일 확인 버튼 alert UX */}
          {!alertOnly && (
            <button
              type="button"
              onClick={() => resolve(false)}
              className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg)]"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => resolve(true)}
            autoFocus
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${confirmCls}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
